import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Send, Loader2, Palette, History,
  RefreshCw, Save, Trash2, ImageIcon, X, Sparkles,
  MessageSquare
} from 'lucide-react';
import Navigation from '../components/Navigation';
import ItemDetailModal from '../components/ItemDetailModal';
import ProjectCard from '../components/design/ProjectCard';
import ConversationMessage from '../components/design/ConversationMessage';

import { BACKEND_URL } from '../config/api';

const LIFECYCLE_BADGE_STYLES = {
  idea: 'bg-indigo-100 text-indigo-600',
  finished: 'bg-amber-100 text-amber-600',
  shared: 'bg-teal-100 text-teal-600',
};

const SEED_PROMPTS = [
  "Design a cheerful birthday card using bright colors",
  "What can I make with floral stamps and alcohol markers?",
  "Suggest a color palette for a beach themed card",
  "Create a sympathy card with soft watercolor tones",
  "What techniques work well with embossing folders?",
  "Design a thank you card using kraft cardstock",
  "Suggest a layout for a masculine birthday card",
  "What can I make with washi tape and die cuts?",
  "Help me plan a holiday card set for Christmas",
  "Design a baby shower card in pastel colors",
  "What are creative ways to use leftover paper scraps?",
  "Suggest an elegant wedding card design",
];

const CraftCompanion = () => {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  // Chat state
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [generatingImageFor, setGeneratingImageFor] = useState(null);
  const [error, setError] = useState(null);

  // History
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showSavedOnly, setShowSavedOnly] = useState(false);

  // Seed prompt cycling
  const [promptPage, setPromptPage] = useState(0);
  const promptsPerPage = 3;
  const visiblePrompts = SEED_PROMPTS.slice(
    promptPage * promptsPerPage,
    promptPage * promptsPerPage + promptsPerPage
  );
  const cyclePrompts = () => {
    const nextPage = (promptPage + 1) * promptsPerPage >= SEED_PROMPTS.length ? 0 : promptPage + 1;
    setPromptPage(nextPage);
  };

  // Stash items for design matching
  const [stashItemMap, setStashItemMap] = useState({});

  // Item detail modal
  const [selectedItem, setSelectedItem] = useState(null);
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);

  // Refs
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const chatContainerRef = useRef(null);

  useEffect(() => { fetchHistory(); }, []);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (isItemModalOpen) { setIsItemModalOpen(false); setSelectedItem(null); }
        if (showHistory) setShowHistory(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isItemModalOpen, showHistory]);

  const fetchHistory = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/design/history`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.clone().json();
        setHistory(data.suggestions || []);
      }
    } catch (err) {
      console.error('Failed to fetch history:', err);
    }
  };

  const fetchStashItemDetails = useCallback(async (itemsNeeded) => {
    if (!itemsNeeded?.length) return;
    const inStashItems = itemsNeeded.filter(i => i.in_stash && i.item_id);
    if (!inStashItems.length) return;

    const results = await Promise.all(
      inStashItems.map(async (item) => {
        try {
          const res = await fetch(`${BACKEND_URL}/api/stash/${item.item_id}`, {
            credentials: 'include',
            headers: getAuthHeaders()
          });
          if (res.ok) return await res.clone().json();
        } catch (err) {
          console.error(`Failed to fetch item ${item.item_id}:`, err);
        }
        return null;
      })
    );

    const newMap = {};
    results.filter(Boolean).forEach(item => { newMap[item.item_id] = item; });
    setStashItemMap(prev => ({ ...prev, ...newMap }));
  }, [getAuthHeaders]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const text = prompt.trim();
    if (!text || isLoading) return;

    setIsLoading(true);
    setError(null);
    setPrompt('');

    const userMsg = {
      id: `user_${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMsg]);

    try {
      const response = await fetch(`${BACKEND_URL}/api/design/suggest`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ prompt: text, session_id: sessionId })
      });

      if (!response.ok) {
        const errorData = await response.clone().json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to generate response');
      }

      const data = await response.clone().json();
      if (data.session_id) setSessionId(data.session_id);

      const assistantMsg = {
        id: `assistant_${data.suggestion_id}`,
        role: 'assistant',
        response_type: data.response_type || 'project',
        suggestion: data,
        timestamp: data.created_at || new Date().toISOString()
      };
      setMessages(prev => [...prev, assistantMsg]);

      if (data.response_type === 'project' && data.items_needed?.length) {
        fetchStashItemDetails(data.items_needed);
      }
      fetchHistory();
    } catch (err) {
      setError(err.message);
      setMessages(prev => prev.filter(m => m.id !== userMsg.id));
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setSessionId(null);
    setPrompt('');
    setError(null);
    setStashItemMap({});
    inputRef.current?.focus();
  };

  const handleGenerateImage = async (suggestionId) => {
    if (!suggestionId || generatingImageFor) return;
    setGeneratingImageFor(suggestionId);
    try {
      const response = await fetch(`${BACKEND_URL}/api/design/${suggestionId}/generate-image`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        const errorData = await response.clone().json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to generate image');
      }
      const data = await response.clone().json();
      setMessages(prev =>
        prev.map(msg => {
          if (msg.suggestion?.suggestion_id === suggestionId) {
            return { ...msg, suggestion: { ...msg.suggestion, inspiration_image_url: data.inspiration_image_url } };
          }
          return msg;
        })
      );
      fetchHistory();
    } catch (err) {
      setError(err.message);
    } finally {
      setGeneratingImageFor(null);
    }
  };

  const handleSave = async (suggestionId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/design/${suggestionId}/save`, {
        method: 'PUT',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.clone().json();
        setMessages(prev =>
          prev.map(msg => {
            if (msg.suggestion?.suggestion_id === suggestionId) {
              return { ...msg, suggestion: { ...msg.suggestion, saved: data.saved } };
            }
            return msg;
          })
        );
        fetchHistory();
      }
    } catch (err) {
      console.error('Failed to save suggestion:', err);
    }
  };

  const handleDeleteFromHistory = async (suggestionId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/design/${suggestionId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) fetchHistory();
    } catch (err) {
      console.error('Failed to delete suggestion:', err);
    }
  };

  const loadFromHistory = (suggestion) => {
    setMessages([
      { id: `user_history_${suggestion.suggestion_id}`, role: 'user', content: suggestion.prompt, timestamp: suggestion.created_at },
      { id: `assistant_${suggestion.suggestion_id}`, role: 'assistant', response_type: suggestion.response_type || 'project', suggestion, timestamp: suggestion.created_at }
    ]);
    setSessionId(null);
    setShowHistory(false);
    setError(null);
    if (suggestion.items_needed?.length) fetchStashItemDetails(suggestion.items_needed);
  };

  const handleItemClick = (item) => { setSelectedItem(item); setIsItemModalOpen(true); };

  const handleLifecycleChange = (suggestionId, data) => {
    setMessages(prev =>
      prev.map(msg => {
        if (msg.suggestion?.suggestion_id === suggestionId) {
          return {
            ...msg,
            suggestion: {
              ...msg.suggestion,
              lifecycle_state: data.lifecycle_state,
              finished_photo_url: data.finished_photo_url ?? msg.suggestion.finished_photo_url,
              is_shared: data.is_shared ?? msg.suggestion.is_shared,
              share_id: data.share_id ?? msg.suggestion.share_id,
            }
          };
        }
        return msg;
      })
    );
    fetchHistory();
  };

  const isConversationActive = messages.length > 0;
  const filteredHistory = history.filter(item => !showSavedOnly || item.saved);

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <Navigation />

      <div className="flex-1 flex flex-col max-w-3xl mx-auto w-full px-4 lg:px-6">
        {/* Sticky header */}
        <div className="sticky top-0 z-10 bg-cream/95 backdrop-blur-sm border-b border-beige/50 -mx-4 lg:-mx-6 px-4 lg:px-6">
          <div className="flex items-center justify-between py-3">
            <h1 className="text-lg font-medium text-charcoal" data-testid="craft-companion-title">Craft Companion</h1>
            <div className="flex items-center gap-2">
              {isConversationActive && (
                <button
                  onClick={handleNewChat}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-charcoal bg-white rounded-lg border border-beige hover:border-sage transition-colors"
                  data-testid="new-chat-btn"
                >
                  <RefreshCw size={14} />
                  <span className="hidden sm:inline">New</span>
                </button>
              )}
              <button
                onClick={() => setShowHistory(!showHistory)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                  showHistory ? 'bg-sage text-white border-sage' : 'text-charcoal bg-white border-beige hover:border-sage'
                }`}
                data-testid="history-toggle"
              >
                <History size={14} />
                <span className="hidden sm:inline">History</span>
                {history.length > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${showHistory ? 'bg-white/20' : 'bg-sage text-white'}`}>
                    {history.length}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* History panel */}
        {showHistory && (
          <div className="bg-white rounded-xl border border-beige p-4 mt-3 mb-4 max-h-72 overflow-y-auto flex-shrink-0 shadow-sm" data-testid="history-panel">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-charcoal">Past Ideas</h3>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-xs text-gray-soft">Saved</span>
                  <div className="relative">
                    <input type="checkbox" checked={showSavedOnly} onChange={(e) => setShowSavedOnly(e.target.checked)} className="sr-only peer" data-testid="saved-only-toggle" />
                    <div className="w-8 h-4 bg-beige rounded-full peer peer-checked:bg-sage transition-colors" />
                    <div className="absolute left-0.5 top-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform peer-checked:translate-x-4" />
                  </div>
                </label>
                <button onClick={() => setShowHistory(false)} className="p-1 text-gray-soft hover:text-charcoal"><X size={16} /></button>
              </div>
            </div>
            {filteredHistory.length === 0 ? (
              <p className="text-gray-soft text-sm py-4 text-center">{showSavedOnly ? 'No saved ideas yet' : 'No history yet'}</p>
            ) : (
              <div className="space-y-1">
                {filteredHistory.map((item) => (
                  <div key={item.suggestion_id} className="group flex items-center gap-2 p-2.5 rounded-lg hover:bg-cream transition-colors">
                    <button onClick={() => loadFromHistory(item)} className="flex-1 text-left min-w-0" data-testid={`history-item-${item.suggestion_id}`}>
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-medium text-charcoal truncate">{item.title}</p>
                        {item.lifecycle_state && item.lifecycle_state !== 'idea' && (
                          <span className={`text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded-full flex-shrink-0 ${LIFECYCLE_BADGE_STYLES[item.lifecycle_state] || ''}`}>
                            {item.lifecycle_state}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-soft truncate">{item.prompt}</p>
                    </button>
                    <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      {item.inspiration_image_url && <ImageIcon size={14} className="text-sage" />}
                      <button onClick={(e) => { e.stopPropagation(); handleSave(item.suggestion_id); }}
                        className={`p-1 rounded ${item.saved ? 'text-sage' : 'text-gray-soft hover:text-sage'}`}
                        data-testid={`history-save-${item.suggestion_id}`}><Save size={14} /></button>
                      <button onClick={(e) => { e.stopPropagation(); handleDeleteFromHistory(item.suggestion_id); }}
                        className="p-1 rounded text-gray-soft hover:text-red-500"
                        data-testid={`history-delete-${item.suggestion_id}`}><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Chat area */}
        <div ref={chatContainerRef} className="flex-1 overflow-y-auto space-y-5 pb-4 pt-3 min-h-0" data-testid="chat-area">
          {/* Welcome screen */}
          {!isConversationActive && !isLoading && (
            <div className="flex flex-col items-center justify-center py-8 sm:py-12 slide-up">
              <h2 className="text-xl sm:text-2xl font-medium text-charcoal mb-1 text-center" data-testid="companion-heading">
                Craft Companion
              </h2>
              <p className="text-sm text-rose font-medium mb-4">
                Your creative sidekick for turning stash supplies into beautiful projects.
              </p>
              <div className="text-sm text-gray-soft max-w-md text-center space-y-2 mb-8">
                <p>Not sure what to make next?</p>
                <p>Tell me what you're in the mood to create and I'll suggest card ideas, layouts, techniques, color palettes, and ways to use the supplies already in your stash.</p>
                <p className="text-xs text-gray-soft/70 pt-1">From quick inspiration to step-by-step ideas, I'm here to help you make something great today.</p>
              </div>

              {/* Seed prompts with cycling */}
              <div className="w-full max-w-lg">
                <div className="flex flex-col gap-2">
                  {visiblePrompts.map((example, idx) => (
                    <button
                      key={`${promptPage}-${idx}`}
                      onClick={() => { setPrompt(example); inputRef.current?.focus(); }}
                      className="w-full text-left px-4 py-2.5 text-sm bg-white text-charcoal rounded-lg border border-beige hover:border-sage hover:bg-sage-light/30 transition-all"
                      data-testid={`seed-prompt-${idx}`}
                    >
                      {example}
                    </button>
                  ))}
                </div>
                <button
                  onClick={cyclePrompts}
                  className="mt-3 mx-auto flex items-center gap-1.5 text-xs text-gray-soft hover:text-sage transition-colors"
                  data-testid="cycle-prompts-btn"
                >
                  <RefreshCw size={12} />
                  More suggestions
                </button>
              </div>
            </div>
          )}

          {/* Messages */}
          {messages.map((msg) => (
            <div key={msg.id} className="fade-in">
              {msg.role === 'user' ? (
                <div className="flex justify-end" data-testid={`user-msg-${msg.id}`}>
                  <div className="max-w-md bg-sage text-white px-4 py-3 rounded-xl rounded-br-sm shadow-sm">
                    <p className="text-sm leading-relaxed">{msg.content}</p>
                  </div>
                </div>
              ) : msg.response_type === 'conversation' ? (
                <ConversationMessage suggestion={msg.suggestion} />
              ) : (
                <div data-testid={`assistant-msg-${msg.id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 rounded-full bg-sage-light flex items-center justify-center flex-shrink-0">
                      <Sparkles size={14} className="text-sage" />
                    </div>
                    <span className="text-xs text-gray-soft">Craft Companion</span>
                  </div>
                  <div className="ml-9">
                    <ProjectCard
                      suggestion={msg.suggestion}
                      onSave={handleSave}
                      onGenerateImage={handleGenerateImage}
                      isGeneratingImage={generatingImageFor === msg.suggestion?.suggestion_id}
                      stashItemMap={stashItemMap}
                      onItemClick={handleItemClick}
                      getAuthHeaders={getAuthHeaders}
                      onLifecycleChange={handleLifecycleChange}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Loading */}
          {isLoading && (
            <div className="flex items-start gap-2 fade-in">
              <div className="w-7 h-7 rounded-full bg-sage-light flex items-center justify-center flex-shrink-0">
                <Sparkles size={14} className="text-sage" />
              </div>
              <div className="bg-white rounded-xl rounded-tl-sm px-4 py-3 border border-beige shadow-sm">
                <div className="flex items-center gap-2">
                  <Loader2 size={16} className="text-sage animate-spin" />
                  <span className="text-sm text-gray-soft">Thinking about your request...</span>
                </div>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center justify-between">
              <span>{error}</span>
              <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 ml-2"><X size={16} /></button>
            </div>
          )}

          <div ref={chatEndRef} />
        </div>

        {/* Input area */}
        <div className="flex-shrink-0 pb-4 pt-2">
          {sessionId && messages.length > 0 && (
            <div className="flex items-center justify-center mb-2">
              <span className="text-xs text-gray-soft flex items-center gap-1">
                <MessageSquare size={10} />
                Continuing conversation
              </span>
            </div>
          )}
          <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-beige shadow-sm overflow-hidden" data-testid="chat-input-form">
            <div className="flex items-end gap-2 p-3">
              <textarea
                ref={inputRef}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={isConversationActive ? "Ask a follow-up..." : "What are you in the mood to create?"}
                className="flex-1 resize-none border-0 focus:ring-0 text-charcoal placeholder-gray-soft p-1.5 max-h-28 text-sm bg-transparent"
                rows={1}
                disabled={isLoading}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); } }}
                onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 112) + 'px'; }}
                data-testid="design-prompt-input"
              />
              <button
                type="submit"
                disabled={!prompt.trim() || isLoading}
                className="flex-shrink-0 w-10 h-10 bg-sage text-white rounded-lg flex items-center justify-center hover:bg-sage-dark disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                data-testid="submit-design-btn"
              >
                {isLoading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Item Detail Modal */}
      <ItemDetailModal
        isOpen={isItemModalOpen}
        onClose={() => { setIsItemModalOpen(false); setSelectedItem(null); }}
        item={selectedItem}
        onEdit={() => navigate('/stash')}
        BACKEND_URL={BACKEND_URL}
      />
    </div>
  );
};

export default CraftCompanion;
